// src/views/whatsappInbox/FilterDropdown.tsx
import React, { useState } from 'react';
import { Dropdown, Button, Menu, DatePicker, Select, Checkbox, Divider, Space, Tag } from 'antd';
import { FilterOutlined, CloseOutlined, RobotOutlined, MailOutlined, UserOutlined, CheckCircleOutlined, SoundOutlined, CalendarOutlined, TagOutlined, ClearOutlined, CheckOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { setFilter, selectFilters } from '../../store/slices/conversationsSlice';
import moment from 'moment';
import logger from '../../utils/logger';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface FilterDropdownProps {
  onFilterChange?: (filters: any) => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ onFilterChange }) => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectFilters);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  // Local state for pending filter changes (not yet applied)
  const [pendingFilters, setPendingFilters] = useState<any>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Get user preferences for status and labels
  const userPreferences = useAppSelector((state: any) => {
    const preferences = state.rootReducers?.user?.userPreferences;
    if (!preferences || typeof preferences !== 'object') {
      return { labels: [], status: [] };
    }
    return {
      labels: (preferences as any).labels || [],
      status: (preferences as any).status || []
    };
  });

  // Initialize pending filters when dropdown opens
  React.useEffect(() => {
    if (dropdownVisible) {
      setPendingFilters({ ...filters });
      setHasChanges(false);
    }
  }, [dropdownVisible, filters]);

  const handleFilterToggle = (filterKey: string) => {
    let value;

    // Special cases for filter values
    if (filterKey === 'isRead' || filterKey === 'isAssigned') {
      value = pendingFilters[filterKey as keyof typeof pendingFilters] === false ? undefined : false;
    } else {
      value = pendingFilters[filterKey as keyof typeof pendingFilters] ? undefined : true;
    }

    setPendingFilters({ ...pendingFilters, [filterKey]: value });
    setHasChanges(true);
  };

  const handleDateRangeChange = (dates: any, dateStrings: string[]) => {
    if (dates) {
      setPendingFilters({
        ...pendingFilters,
        startDate: dateStrings[0],
        endDate: dateStrings[1]
      });
    } else {
      const { startDate, endDate, ...rest } = pendingFilters;
      setPendingFilters(rest);
    }
    setHasChanges(true);
  };

  const handleDateRangeForChange = (value: string) => {
    setPendingFilters({
      ...pendingFilters,
      dateRangeFor: value || undefined
    });
    setHasChanges(true);
  };

  const handleChannelChange = (value: string) => {
    setPendingFilters({
      ...pendingFilters,
      channel: value || undefined
    });
    setHasChanges(true);
  };

  // Handle status filter toggle - updates pending state only
  const handleStatusToggle = (statusValue: string) => {
    const currentStatuses = pendingFilters.leadStatus || [];
    let newStatuses: string[];

    if (currentStatuses.includes(statusValue)) {
      newStatuses = currentStatuses.filter((s: string) => s !== statusValue);
      logger.log(`🏷️ Removed status: ${statusValue}`);
    } else {
      newStatuses = [...currentStatuses, statusValue];
      logger.log(`🏷️ Added status: ${statusValue}`);
    }

    logger.log('🏷️ Updated leadStatus:', newStatuses);
    setPendingFilters({
      ...pendingFilters,
      leadStatus: newStatuses.length > 0 ? newStatuses : undefined
    });
    setHasChanges(true);
  };

  // Handle label filter toggle - updates pending state only
  const handleLabelToggle = (labelValue: string) => {
    const currentLabels = pendingFilters.leadLabel || [];
    let newLabels: string[];

    if (currentLabels.includes(labelValue)) {
      newLabels = currentLabels.filter((l: string) => l !== labelValue);
      logger.log(`🏷️ Removed label: ${labelValue}`);
    } else {
      newLabels = [...currentLabels, labelValue];
      logger.log(`🏷️ Added label: ${labelValue}`);
    }

    logger.log('🏷️ Updated leadLabel:', newLabels);
    setPendingFilters({
      ...pendingFilters,
      leadLabel: newLabels.length > 0 ? newLabels : undefined
    });
    setHasChanges(true);
  };

  // Apply filters - dispatches to Redux and triggers API call
  const applyFilters = () => {
    logger.log('📤 Applying WhatsApp Inbox Filters:', {
      pendingFilters,
      leadStatus: pendingFilters.leadStatus,
      leadLabel: pendingFilters.leadLabel
    });

    Object.keys(pendingFilters).forEach(key => {
      dispatch(setFilter({ key: key as any, value: pendingFilters[key] }));
    });

    logger.log('✅ Filters dispatched to Redux - API call will be triggered');
    setHasChanges(false);
    setDropdownVisible(false);
  };

  // Clear all pending filters
  const clearAllPendingFilters = () => {
    setPendingFilters({});
    setHasChanges(true);
  };

  // Cancel changes and close dropdown
  const cancelChanges = () => {
    setPendingFilters({ ...filters });
    setHasChanges(false);
    setDropdownVisible(false);
  };

  // Quick reset button (clears applied filters without opening dropdown)
  const quickResetFilters = () => {
    Object.keys(filters).forEach(key => {
      if (key !== 'search') {
        dispatch(setFilter({ key: key as any, value: undefined }));
      }
    });
  };

  const getActiveFilterCount = () => {
    return Object.keys(filters).filter(key => key !== 'search' && filters[key as keyof typeof filters] !== undefined).length;
  };

  const filterItems = [
    { key: 'isAutomation', label: 'Automations', icon: <RobotOutlined />, active: pendingFilters.isAutomation },
    { key: 'isRead', label: 'Unread', icon: <MailOutlined />, active: pendingFilters.isRead === false },
    { key: 'isAssigned', label: 'Unassigned', icon: <UserOutlined />, active: pendingFilters.isAssigned === false },
    { key: 'isAssignedToMe', label: 'My inbox', icon: <CheckCircleOutlined />, active: pendingFilters.isAssignedToMe },
    { key: 'isInCampaignInbox', label: 'Campaigns', icon: <SoundOutlined />, active: pendingFilters.isInCampaignInbox }
  ];

  const menu = (
    <div className="modern-filter-dropdown"
      style={{
        width: 320,
        maxHeight: '80vh',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        border: '1px solid #f0f0f0',
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header - Fixed */}
      <div style={{
        padding: '18px 24px 14px',
        borderBottom: '1px solid #f5f5f5',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        flexShrink: 0
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontWeight: 600,
          fontSize: '15px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        }}>
          <FilterOutlined style={{ fontSize: '14px' }} />
          Filter Conversations
        </div>
        <div style={{
          fontSize: '12px',
          opacity: 0.9,
          marginTop: '5px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          fontWeight: 400
        }}>
          {getActiveFilterCount()} filters active
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
      {/* Quick Filters */}
      <div style={{ padding: '18px 24px' }}>
        <div style={{ 
          fontSize: '11px', 
          fontWeight: 600, 
          color: '#8c8c8c', 
          marginBottom: '14px',
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        }}>
          Quick Filters
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {filterItems.map(item => (
            <Button
              key={item.key}
              size="small"
              type={item.active ? 'primary' : 'default'}
              onClick={() => handleFilterToggle(item.key)}
              style={{
                borderRadius: '22px',
                height: '34px',
                padding: '0 16px',
                border: item.active ? 'none' : '1px solid #e8e8e8',
                backgroundColor: item.active ? '#1890ff' : '#fafafa',
                color: item.active ? '#fff' : '#595959',
                fontSize: '12px',
                fontWeight: 500,
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                boxShadow: item.active ? '0 2px 8px rgba(24, 144, 255, 0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
              }}
              icon={React.cloneElement(item.icon, { style: { fontSize: '12px' } })}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </div>

      <Divider style={{ margin: 0, backgroundColor: '#f5f5f5' }} />

      {/* Date Range */}
      <div style={{ padding: '18px 24px' }}>
        <div style={{ 
          fontSize: '11px', 
          fontWeight: 600, 
          color: '#8c8c8c', 
          marginBottom: '14px',
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        }}>
          <CalendarOutlined style={{ fontSize: '12px' }} />
          Date Range
        </div>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Select
            placeholder="Filter by..."
            value={pendingFilters.dateRangeFor}
            onChange={handleDateRangeForChange}
            style={{
              width: '100%',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            }}
            allowClear
            size="middle"
            suffixIcon={<CalendarOutlined style={{ color: '#bfbfbf', fontSize: '12px' }} />}
          >
            <Option value="firstMessage">📥 First Message</Option>
            <Option value="lastMessage">📤 Last Message</Option>
          </Select>
          <RangePicker
            style={{
              width: '100%',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            }}
            onChange={handleDateRangeChange}
            value={pendingFilters.startDate && pendingFilters.endDate ? [
              moment(pendingFilters.startDate),
              moment(pendingFilters.endDate)
            ] : null}
            size="middle"
            placeholder={['Start Date', 'End Date']}
          />
        </Space>
      </div>

      <Divider style={{ margin: 0, backgroundColor: '#f5f5f5' }} />

      {/* Lead Status Filter */}
      {userPreferences.status && userPreferences.status.length > 0 && (
        <>
          <div style={{ padding: '18px 24px' }}>
            <div style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#8c8c8c',
              marginBottom: '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            }}>
              <CheckCircleOutlined style={{ fontSize: '12px' }} />
              Lead Status
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {userPreferences.status.map((status: any) => {
                const isActive = pendingFilters.leadStatus?.includes(status.value);
                return (
                  <Tag
                    key={status.value}
                    onClick={() => handleStatusToggle(status.value)}
                    style={{
                      cursor: 'pointer',
                      borderRadius: '18px',
                      padding: '6px 14px',
                      fontSize: '12px',
                      fontWeight: isActive ? 700 : 600,
                      border: `2px solid ${status.color}`,
                      backgroundColor: isActive ? status.color : `${status.color}25`,
                      color: isActive ? '#fff' : status.color,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease',
                      boxShadow: isActive ? `0 3px 10px ${status.color}50` : '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    {isActive && <CheckOutlined style={{ fontSize: '11px', fontWeight: 'bold' }} />}
                    {status.name}
                  </Tag>
                );
              })}
            </div>
          </div>
          <Divider style={{ margin: 0, backgroundColor: '#f5f5f5' }} />
        </>
      )}

      {/* Lead Label Filter */}
      {userPreferences.labels && userPreferences.labels.length > 0 && (
        <>
          <div style={{ padding: '18px 24px' }}>
            <div style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#8c8c8c',
              marginBottom: '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            }}>
              <TagOutlined style={{ fontSize: '12px' }} />
              Lead Labels
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {userPreferences.labels.map((label: any) => {
                const isActive = pendingFilters.leadLabel?.includes(label.value);
                return (
                  <Tag
                    key={label.value}
                    onClick={() => handleLabelToggle(label.value)}
                    style={{
                      cursor: 'pointer',
                      borderRadius: '18px',
                      padding: '6px 14px',
                      fontSize: '12px',
                      fontWeight: isActive ? 700 : 600,
                      border: `2px solid ${label.color}`,
                      backgroundColor: isActive ? label.color : `${label.color}25`,
                      color: isActive ? '#fff' : label.color,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease',
                      boxShadow: isActive ? `0 3px 10px ${label.color}50` : '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    {isActive && <CheckOutlined style={{ fontSize: '11px', fontWeight: 'bold' }} />}
                    {label.name}
                  </Tag>
                );
              })}
            </div>
          </div>
          <Divider style={{ margin: 0, backgroundColor: '#f5f5f5' }} />
        </>
      )}
      </div>

      {/* Footer - Fixed with Apply/Cancel */}
      <div style={{
        padding: '16px 24px',
        backgroundColor: '#fafafa',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        borderTop: '1px solid #f0f0f0',
        gap: '12px'
      }}>
        <Button
          type="link"
          danger
          onClick={clearAllPendingFilters}
          disabled={Object.keys(pendingFilters).length === 0}
          style={{
            fontSize: '12px',
            padding: '6px 12px',
            height: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            fontWeight: 500,
            borderRadius: '6px'
          }}
          icon={<ClearOutlined style={{ fontSize: '11px' }} />}
        >
          Clear
        </Button>

        <div style={{ display: 'flex', gap: '8px', flex: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={cancelChanges}
            style={{
              fontSize: '12px',
              padding: '6px 16px',
              height: '36px',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              fontWeight: 500,
              borderRadius: '8px',
              border: '1px solid #d9d9d9'
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={applyFilters}
            disabled={!hasChanges}
            style={{
              fontSize: '12px',
              padding: '6px 20px',
              height: '36px',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              fontWeight: 600,
              borderRadius: '8px',
              background: hasChanges ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : undefined,
              border: 'none',
              boxShadow: hasChanges ? '0 2px 8px rgba(102, 126, 234, 0.4)' : undefined
            }}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );

  const activeCount = getActiveFilterCount();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {/* Quick Reset Button - shows when filters are active */}
      {activeCount > 0 && (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            quickResetFilters();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            padding: 0,
            border: '1px solid #ff4d4f',
            color: '#ff4d4f',
            backgroundColor: '#fff1f0',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#ffccc7';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#fff1f0';
          }}
          title="Clear all filters"
        >
          <CloseOutlined style={{ fontSize: '14px' }} />
        </Button>
      )}

      <Dropdown
        overlay={menu}
        trigger={['click']}
        visible={dropdownVisible}
        onVisibleChange={setDropdownVisible}
        placement="bottomLeft"
      >
        <Button
        className={`modern-filter-button ${activeCount > 0 ? 'has-filters' : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          borderRadius: '8px',
          height: '36px',
          padding: '0 14px',
          fontSize: '12px',
          fontWeight: 500,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          background: activeCount > 0 
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
            : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          color: activeCount > 0 ? 'white' : '#495057',
          border: activeCount > 0 ? 'none' : '1px solid #dee2e6',
          boxShadow: activeCount > 0 
            ? '0 4px 12px rgba(102, 126, 234, 0.4)' 
            : '0 2px 4px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          if (activeCount === 0) {
            e.currentTarget.style.background = 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }
        }}
        onMouseLeave={(e) => {
          if (activeCount === 0) {
            e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
      >
        <FilterOutlined style={{ fontSize: '12px' }} />
        Filters
        {activeCount > 0 && (
          <span
            style={{
              backgroundColor: 'rgba(255,255,255,0.3)',
              borderRadius: '12px',
              padding: '2px 8px',
              fontSize: '10px',
              fontWeight: 'bold',
              minWidth: '18px',
              textAlign: 'center'
            }}
          >
            {activeCount}
          </span>
        )}
      </Button>
    </Dropdown>
    </div>
  );
};

export default FilterDropdown;